import React, { cloneElement } from "react";
import {
  TextInput,
  SimpleForm,
  AutocompleteInput,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  EditContextProvider,
  useEditController,
  EditProps,
  Title,
} from "react-admin";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import DaysOfWeekInput from "components/DaysOfWeekInput";
import { arrivalTimeLabel, schoolTimeLabel, typeList } from "./util";
import _ from "lodash";
import { GetParam } from "utils/tools";
import { labelStyle } from "utils/style";
import CustomEditToolbar from "components/CustomEditToolbar";
import CustomTimePickerInput from "components/CustomTimePickerInput";

const BusScheduleEditForm = (props: any) => {
  const controllerProps = useEditController(props);
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
    defaultTitle,
  } = controllerProps;

  const formatRecord = (res: any) => {
    if (res) {
      if (res.levels) {
        //parse route tags data to be prefillable by select array input
        const temp = _.cloneDeep(res.levels);
        const levelIds = temp.map((item: any) => {
          return item ? item.id : item;
        });
        res.levelIds = levelIds;
      }
    }
    return res;
  };

  return (
    <EditContextProvider value={controllerProps}>
      <div>
        <Typography variant="h4" style={labelStyle}>
          {record?.name}
        </Typography>

        <Title title={defaultTitle} />

        {loaded &&
          cloneElement(props.children, {
            className: "MuiPaper-root MuiPaper-elevation1",
            basePath,
            record: formatRecord(record),
            redirect,
            resource,
            save,
            saving,
            version,
          })}
      </div>
    </EditContextProvider>
  );
};

const BusSchedulesEdit = (props: EditProps) => {
  const { id } = props;
  const param = GetParam();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    {
      title: "Bus Schedules",
      link: `/schools/${param.get("schoolId")}/show/2`,
    },
    {
      title: id,
      link: ``,
    },
  ];

  const transform = async (data: any) => ({
    ...data,
    isMonday: data.isMonday ? true : false,
    isTuesday: data.isTuesday ? true : false,
    isWednesday: data.isWednesday ? true : false,
    isThursday: data.isThursday ? true : false,
    isFriday: data.isFriday ? true : false,
  });

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <BusScheduleEditForm transform={transform} {...props} undoable={false}>
        <SimpleForm
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/2`}
              isDeleteWithConfirm
            />
          }
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School and Type Schedule</Typography>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={4}>
              <SelectInput source="type" choices={typeList} disabled />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput source="name" label="Wave Name" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={8}>
              <ReferenceArrayInput
                source="levelIds"
                label="Select Levels"
                reference="levels"
                filter={{ SchoolId: Number(param.get("schoolId")) }}
              >
                <AutocompleteArrayInput optionText="name" />
              </ReferenceArrayInput>
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <CustomTimePickerInput
                source="schoolTime"
                label={`${schoolTimeLabel(param.get("type"))}`}
              />
            </Grid>
            <Grid item xs={4}>
              <CustomTimePickerInput
                source="arrivalTime"
                label={`${arrivalTimeLabel(param.get("type"))}`}
              />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Days of Week</Typography>
            </Grid>

            <DaysOfWeekInput />
          </SanitizedGrid>
        </SimpleForm>
      </BusScheduleEditForm>
    </>
  );
};

export default BusSchedulesEdit;
